import React, { useEffect, useState } from 'react'

import { get } from 'lodash'

import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import { Button, Typography, Form, message, Badge, Tooltip } from 'antd'

import { observer } from 'mobx-react-lite'

import { FlexBoxX, FlexBoxY, Box } from 'common/components/boxes'
import { Page } from 'common/components/Page'
import { TextArea } from 'common/components/TextArea'
import Uploader from 'common/components/uploader'

import { useVendorStores } from 'vendor/hooks/use-stores'

export const PublicProfile = observer(() => {
  const { uploaderStore, userStore, vendorSettingStore } = useVendorStores()

  const [isSubmitting, setSubmitting] = useState(false)

  const [form] = Form.useForm()

  const handleFinish = async ({ description = '' }) => {
    setSubmitting(true)
    try {
      const logoSignedId = get(uploaderStore.signedIds('logo'), '[0]')
      const vendor = await vendorSettingStore.updateCurrentVendor({ description, logo_signed_id: logoSignedId })
      userStore.currentUser.vendor = vendor
      message.success('Updated public profile settings')
    } catch (error) {
      console.error('Unable to save changes', error?.response?.data)
      message.error('Unable to save changes')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    form.setFieldsValue({ description: userStore.currentUser?.vendor.description })
  }, [userStore.currentUser?.vendor.description])

  useEffect(() => {
    uploaderStore.resetUploads('logo')
    uploaderStore.addExistingFiles('logo', userStore.currentUser?.vendor.logo)
  }, [userStore.currentUser?.vendor?.logo?.key])

  return (
    <Form
      form={form}
      initialValues={{ description: userStore.currentUser?.vendor.description }}
      onFinish={handleFinish}
      style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
    >
      <Page.Header>
        <FlexBoxY alignItems="flex-start">
          <FlexBoxX width="100%" justifyContent="space-between">
            <Typography.Title level={3} style={{ flexGrow: 1 }}>
              Public Profile
            </Typography.Title>

            <Form.Item style={{ margin: 0 }}>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Save Changes
              </Button>
            </Form.Item>
          </FlexBoxX>

          <Typography.Paragraph style={{ margin: 0 }}>
            Add a short description so that contractors can get to know your company.
          </Typography.Paragraph>
        </FlexBoxY>
      </Page.Header>

      <Page.Content>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 150, message: 'Description cannot be longer than 150 characters!' },
            { required: true, message: 'Please input a company description!' },
          ]}
          style={{
            width: '100%',
          }}
        >
          <TextArea
            showCount
            rows={3}
            maxLength={150}
            placeholder="Premier lighting distributor in Nevada"
            style={{ resize: 'none' }}
          />
        </Form.Item>

        <Box
          display="flex"
          flexDirection={{ _: 'column', xs: 'row' }}
          alignItems={{ _: 'flex-start', xs: 'center' }}
          mt={40}
          ml={{ _: 0, xs: '39px' }}
        >
          <Box mr={20} style={{ whiteSpace: 'nowrap' }}>
            <Badge
              style={{ marginRight: 20 }}
              count={
                <Tooltip
                  placement="top"
                  title="The contractors will see this logo when they search your company. Should use small images (i.e 32x32)."
                >
                  <QuestionCircleOutlined style={{ top: -5, right: -5 }} />
                </Tooltip>
              }
            >
              <Typography.Paragraph>Logo:</Typography.Paragraph>
            </Badge>
          </Box>
          <Uploader
            modalTitle="Edit logo"
            accept="image/png, image/jpg, image/jpeg"
            component="Upload"
            multiple={false}
            uploadKey="logo"
            noResetUploads
            listType="picture-card"
            fileList={uploaderStore.fileList('logo')}
            onRemoveUpload={uploaderStore.removeUpload}
            onAddNewUpload={uploaderStore.addNewUpload}
            onResetUploads={uploaderStore.resetUploads}
            onSetUploadError={uploaderStore.setUploadError}
            onUpdateUpload={uploaderStore.updateUpload}
          />
        </Box>
      </Page.Content>
    </Form>
  )
})
